import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {taskSetColour} from './taskSetColours';
import {TaskSetRepeatIcon} from './TaskSetRepeatIcon';

/**
 * A pill, not a button-looking control: it sits inside a dense table row under
 * the task name, and anything with a border or a hover lift would compete with
 * the row's own controls. 19px tall so the row grows by one text line, no more.
 */
const Pill = styled.button<{background: string; foreground: string}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	height: 19px;
	padding: 0 8px 0 6px;
	border: none;
	border-radius: 9.5px;
	background: ${({background}) => background};
	color: ${({foreground}) => foreground};
	cursor: pointer;
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;

	&:focus-visible {
		outline: 2px solid ${({foreground}) => foreground};
		outline-offset: 1px;
	}
`;

export interface TaskSetChipProps {
	taskSetId: string;
	/** 1-based position of this instance among the set's current tasks. */
	sequence?: number | null;
	/** How many tasks the set has now. */
	instanceCount?: number | null;
	onOpen: (taskSetId: string) => void;
}

/**
 * Marks a task as an instance of a set, and opens that set's config view.
 *
 * Deliberately minimal: an icon, and the position in the set. It carries no set
 * name and no edited state. It is on every instance row, so anything more turns
 * a dense table into noise -- and the name is one click away in the view it
 * opens, which is where there is room for it.
 *
 * Being the way in is its third job, not an afterthought: the config view has two
 * entry points and this is the one that works while the reader is looking at the
 * work rather than at a list of rules.
 */
export const TaskSetChip = ({taskSetId, sequence, instanceCount, onOpen}: TaskSetChipProps) => {
	const intl = useIntl();
	const colour = taskSetColour(taskSetId);

	// Both halves or neither. "3 of" reads as truncated, and a bare "3" says
	// nothing about how long the series runs.
	//
	// The total is the tasks the set has now, and the position is the task's place
	// among them: a deleted task's gap closes, so the position never exceeds the
	// total. The guard stays for data that cannot be trusted to agree -- a missing
	// or out-of-range position drops the label, because no label beats a false
	// one. The icon alone still says the task came from a set.
	const total = instanceCount;
	const hasCoherentPosition = sequence != null && total != null && sequence >= 1 && sequence <= total;
	const label = hasCoherentPosition ? intl.formatMessage({id: 'task_set.instance_sequence'}, {sequence, total}) : null;

	return (
		<Pill
			type="button"
			background={colour.background}
			foreground={colour.foreground}
			onClick={event => {
				// The row opens the task modal on click. Without this, marking an
				// instance would also open the task behind the drawer.
				event.stopPropagation();
				onOpen(taskSetId);
			}}
			data-cy="task-set-chip"
			aria-label={intl.formatMessage({id: 'task_set.manage'})}
			title={intl.formatMessage({id: 'task_set.manage'})}
		>
			<TaskSetRepeatIcon />
			{label ? <span>{label}</span> : null}
		</Pill>
	);
};
